import {
  CrashApiError,
  type CrashApiErrorKind,
} from '~/adapters/api-crash/crash-api';
import { fetchCrashPreGameHandler } from '~/handlers/fetch-crash-pre-game.handler';
import type { CrashGameName, CrashPreGame } from '~/types/crash-pre-game';

// Polling da tela de pré-jogo dos crash games (ENG-833).
//
// 10s por pedido do Mario (o guia de integração sugeria 2–5s). O core mantém
// cache de 2s na rota, então o ritmo não muda o frescor do dado: muda quantas
// vezes cada jogador bate na rota. Uma vela do Aviator sai a cada ~10s, então
// este é o ritmo em que a tela ainda acompanha o jogo gastando uma request por
// rodada — e sobra bastante do rate limit de 120 req/min por IP, que em CGNAT é
// dividido por vários jogadores mobile.
const POLL_INTERVAL_MS = 10000;

// Backoff em falha: dobra a cada tentativa até este teto. Em CGNAT vários
// jogadores mobile dividem o mesmo IP, então insistir no mesmo ritmo depois de
// um 429 só prolonga o castigo pra todos eles. Precisa ficar acima do ritmo
// normal pra que a primeira falha realmente espace as tentativas.
const MAX_BACKOFF_MS = 60000;

// Jogo SEM NENHUMA vela: existe na whitelist do core (config/crash.php lista
// `aviator` e `aviator-2`), então a rota responde 200 com `cycleHistory: []` —
// não 422. É o caso do coletor desligado pra aquela instância, que hoje é o
// Aviator 1 em produção. A tela não mostra nada nesse estado, e insistir no
// ritmo normal numa tela vazia é request à toa; o poller cai pra este ritmo e
// volta ao normal sozinho na primeira vela que aparecer.
const IDLE_INTERVAL_MS = 60000;

// O Aviator roda uma vela a cada ~10s. Um minuto sem vela nova não é jogo
// devagar, é captura parada — a tela avisa que o histórico está velho em vez de
// deixar o jogador achar que aquela é a última rodada.
const STALE_AFTER_SECONDS = 60;

// Uma hora sem vela nova não é jogo parado por uns minutos: é coletor
// abandonado. A partir daí a tela inteira some, em vez de ficar servindo um
// histórico de ontem com cara de agora — o jogador não tem como saber que aquilo
// envelheceu, e um painel de estatística velha é pior que painel nenhum.
const ABANDONED_AFTER_SECONDS = 3600;

export type CrashPreGameStatus = 'idle' | 'loading' | 'ready' | 'error';

type CrashPreGamePoller = {
  data: Ref<CrashPreGame | null>;
  status: Ref<CrashPreGameStatus>;
  errorKind: Ref<CrashApiErrorKind | null>;
  now: Ref<number>;
  acquire: () => void;
  release: () => void;
  retry: () => void;
};

// UM poller por jogo, compartilhado por todos os blocos da tela.
//
// O desenho da tela espalha o mesmo payload em containers diferentes ("pagou
// hoje" e as pills dentro do card do jogo, a grade num card próprio, e o
// desktop repetindo a dupla) — e blocos irmãos não alcançam um provide/inject.
// Se cada bloco tivesse seu próprio polling, o mesmo jogador faria 3x as
// requests do orçamento de rate limit e ainda veria os blocos com dados de
// ticks diferentes por alguns instantes.
//
// Por isso: instância por `game_name`, contada por referência. O primeiro bloco
// que monta liga o polling; o último que desmonta desliga.
const pollers = new Map<CrashGameName, CrashPreGamePoller>();

// Jogos que já responderam SEM NENHUMA vela nesta sessão. O poller é destruído
// quando o último bloco desmonta, então sem esta memória cada volta à página do
// Aviator (coletor desligado) mostraria o skeleton de novo — um pulo de layout
// numa tela que termina vazia. Com ela, a segunda visita em diante não pisca
// nada. Some no reload, que é quando vale reavaliar se o coletor voltou.
const knownEmptyGames = new Set<CrashGameName>();

function createPoller(gameName: CrashGameName): CrashPreGamePoller {
  const data = ref<CrashPreGame | null>(null);
  const status = ref<CrashPreGameStatus>('idle');
  const errorKind = ref<CrashApiErrorKind | null>(null);

  // Relógio local pro "atualizado há Xs" e pra régua de dado velho. Sem ele os
  // dois congelam entre respostas do polling: quando o coletor cai, é
  // justamente aí que o contador precisa continuar andando.
  const now = ref(Date.now());

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let clockTimer: ReturnType<typeof setInterval> | null = null;
  let controller: AbortController | null = null;
  let consecutiveFailures = 0;
  let isActive = false;
  let consumers = 0;
  let isFetching = false;

  // Último `Retry-After` que o servidor mandou, guardado porque o backoff é
  // recalculado fora do catch (na volta da aba, por exemplo) e a janela do rate
  // limit continua valendo até ela virar.
  let pendingRetryAfterSeconds: number | null = null;

  // Polling encerrado de vez pelo 422: jogo fora da whitelist não vira jogo da
  // whitelist tentando de novo. Marcado à parte de `isActive` pra que um bloco
  // que monte depois (o card do histórico abrindo depois das pills) não religue
  // o polling pra receber o mesmo 422 — o erro já está na tela pra ele ler.
  let isTerminated = false;

  const isVisible = () =>
    typeof document === 'undefined' || document.visibilityState === 'visible';

  function clearPollTimer() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  function startClock() {
    if (clockTimer || typeof window === 'undefined') return;

    clockTimer = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  }

  function stopClock() {
    if (clockTimer) {
      clearInterval(clockTimer);
      clockTimer = null;
    }
  }

  function nextDelay(): number {
    if (consecutiveFailures === 0) return POLL_INTERVAL_MS;

    const exponential = Math.min(
      POLL_INTERVAL_MS * Math.pow(2, consecutiveFailures),
      MAX_BACKOFF_MS,
    );

    // O servidor manda o `Retry-After` só no 429, e quando manda ele VENCE, sem
    // teto local: é a única fonte que sabe quando a janela do rate limit vira.
    // Cortar um `Retry-After: 120` no teto de 30s faria a próxima tentativa cair
    // dentro da janela fechada e voltar 429 — o backoff nunca sairia do teto.
    if (pendingRetryAfterSeconds !== null) {
      return Math.max(pendingRetryAfterSeconds * 1000, exponential);
    }

    return exponential;
  }

  function scheduleNext(delayMs: number) {
    clearPollTimer();

    if (!isActive) return;

    pollTimer = setTimeout(() => {
      pollTimer = null;
      // Aba escondida no momento do tick: não busca e não reagenda — quem
      // retoma é o listener de visibilidade, com um fetch imediato.
      if (!isVisible()) return;

      tick();
    }, delayMs);
  }

  async function tick() {
    if (!isActive) return;

    // Uma request por vez: aborta a anterior antes de abrir a próxima, senão um
    // tick lento e um rápido podem chegar fora de ordem e a tela volta no
    // tempo.
    controller?.abort();
    controller = new AbortController();

    const requestController = controller;

    isFetching = true;

    if (!data.value) status.value = 'loading';

    try {
      const result = await fetchCrashPreGameHandler({
        gameName,
        signal: requestController.signal,
      });

      if (!isActive) return;

      data.value = result;
      status.value = 'ready';
      errorKind.value = null;
      consecutiveFailures = 0;
      pendingRetryAfterSeconds = null;
      now.value = Date.now();

      if (result.cycleHistory.length === 0) {
        knownEmptyGames.add(gameName);
        scheduleNext(IDLE_INTERVAL_MS);
      } else {
        // Coletor voltou: esquece a marca e retoma o ritmo normal.
        knownEmptyGames.delete(gameName);
        scheduleNext(POLL_INTERVAL_MS);
      }
    } catch (error) {
      // Aborto nosso (último consumidor desmontou, request substituída) não é
      // falha: sai sem tocar no estado e sem reagendar.
      if (requestController.signal.aborted) return;
      if (!isActive) return;

      const kind: CrashApiErrorKind =
        error instanceof CrashApiError ? error.kind : 'api';

      errorKind.value = kind;
      consecutiveFailures += 1;

      // Com payload em tela o estado segue 'ready': a falha é um aviso
      // discreto, não uma troca de tela. 'error' é só pra quem nunca viu dado.
      status.value = data.value ? 'ready' : 'error';

      // 422 = `game_name` fora da whitelist (ou ausente). É bug de chamada, não
      // condição passageira: insistir gastaria rate limit pra sempre receber
      // 422. Para o polling de vez e deixa o erro na tela.
      if (kind === 'invalid_game') {
        isTerminated = true;
        stop();

        return;
      }

      pendingRetryAfterSeconds =
        error instanceof CrashApiError ? error.retryAfterSeconds : null;

      scheduleNext(nextDelay());
    } finally {
      // Só a request MAIS NOVA baixa a flag: uma que foi substituída no meio do
      // caminho chega aqui depois, e limpar a flag dela diria que não há request
      // em vôo quando há.
      if (controller === requestController) isFetching = false;
    }
  }

  function onVisibilityChange() {
    if (!isActive) return;

    if (isVisible()) {
      startClock();
      now.value = Date.now();

      // Já tem request em vôo (a aba piscou no meio de um tick): a resposta
      // dela está chegando e reagendar é o trabalho dela.
      if (isFetching) return;

      // Voltar com o polling em backoff não zera o backoff: o motivo da espera
      // (429, API fora) não some porque o jogador trocou de aba, e o `focus` da
      // aba seria a brecha por onde o rate limit voltaria a estourar.
      if (consecutiveFailures > 0) {
        scheduleNext(nextDelay());

        return;
      }

      // Volta pedindo agora: o payload em tela envelheceu o tempo todo em que a
      // aba ficou escondida. O `clearPollTimer` evita a request duplicada de um
      // tick já agendado pra dentro de instantes.
      clearPollTimer();
      tick();

      return;
    }

    // Escondida: para o timer e o relógio. A request em vôo, se houver, termina
    // sozinha — abortar aqui só descartaria dado que já foi pago.
    clearPollTimer();
    stopClock();
  }

  function start() {
    if (isActive || isTerminated || typeof window === 'undefined') return;

    isActive = true;
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (isVisible()) {
      startClock();
      tick();

      return;
    }

    // Montou com a aba em background (pré-render, aba nova ao fundo): fica
    // armado e a primeira busca sai no `visibilitychange`.
    status.value = 'loading';
  }

  function stop() {
    if (!isActive) return;

    isActive = false;
    clearPollTimer();
    stopClock();
    controller?.abort();
    controller = null;

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function acquire() {
    consumers += 1;

    // `!isActive` e não `consumers === 1`: depois de um 422 o polling está
    // parado com consumidores ainda montados, e um bloco novo entrando não pode
    // se ligar a um poller morto sem que ninguém religue. Quem decide se religar
    // faz sentido é o `start` (que respeita o `isTerminated` do 422).
    if (!isActive) start();
  }

  function release() {
    consumers = Math.max(0, consumers - 1);

    if (consumers > 0) return;

    stop();

    // Sai do mapa junto com o último consumidor: o payload guardado só
    // envelheceria fora da tela, e reabrir o pré-jogo dez minutos depois tem que
    // mostrar velas de agora, não as da visita anterior.
    pollers.delete(gameName);
  }

  /** Nova tentativa manual (botão do estado de erro). Zera o backoff. */
  function retry() {
    consecutiveFailures = 0;
    pendingRetryAfterSeconds = null;

    // Limpa o erro NA HORA: sem isso o toque no botão não muda nada na tela (a
    // mensagem de erro continua, porque é ela que `isFatalError` renderiza) e o
    // jogador não tem como saber que a tentativa saiu. Sem dado, o lugar do erro
    // passa a ser o skeleton, que é o estado real: buscando.
    errorKind.value = null;

    if (!data.value) status.value = 'loading';

    if (!isActive) {
      start();

      return;
    }

    // Request em vôo é tentativa: deixa terminar em vez de abortar e recomeçar —
    // tocar duas vezes no botão cancelaria a busca que já estava acontecendo.
    if (isFetching) return;

    clearPollTimer();
    tick();
  }

  return { data, status, errorKind, now, acquire, release, retry };
}

function getPoller(gameName: CrashGameName): CrashPreGamePoller {
  const existing = pollers.get(gameName);

  if (existing) return existing;

  const created = createPoller(gameName);

  pollers.set(gameName, created);

  return created;
}

/**
 * Estado de pré-jogo de UM crash game, pronto pra renderizar.
 *
 * Vários componentes podem chamar isto com o mesmo `gameName` — todos falam com
 * o mesmo polling (uma request por tick, mesmo payload em todos os blocos).
 *
 * Regras que o polling garante, todas vindas do contrato da rota:
 *
 * - Polling só enquanto a aba está visível (`visibilitychange` pausa e retoma).
 * - Request em vôo é abortada quando o último bloco desmonta (`AbortController`).
 * - Falha NÃO limpa a tela: o último payload bom continua renderizado e a
 *   próxima tentativa entra no tick seguinte. Erro cheio só quando nunca houve
 *   um 200.
 * - 422 (`invalid_game`) para o polling: é erro de chamada, repetir não muda o
 *   resultado.
 *
 * @param gameName `game_name` da whitelist, ou null pra ficar inerte (jogo que
 * não é crash) — resolvido por `resolveCrashGameName`.
 */
export function useCrashPreGame(
  gameName: MaybeRefOrGetter<CrashGameName | null>,
) {
  const resolvedGameName = computed(() => toValue(gameName));

  const poller = shallowRef<CrashPreGamePoller | null>(null);

  // Qual jogo este consumidor está segurando. Guarda contra acquire duplicado:
  // se o jogo mudar ANTES do mount, o watch já anexa e o `onMounted` logo
  // depois anexaria de novo — dois acquires pra um release no unmount deixariam
  // o polling rodando pra sempre.
  let attachedName: CrashGameName | null = null;

  function attach() {
    const name = resolvedGameName.value;

    if (!name || attachedName === name) return;

    const next = getPoller(name);

    next.acquire();
    poller.value = next;
    attachedName = name;
  }

  function detach() {
    poller.value?.release();
    poller.value = null;
    attachedName = null;
  }

  // Troca de jogo no mesmo componente (o sheet de pré-jogo é reaproveitado entre
  // jogos): solta o poller do jogo anterior e pega o do novo. Sem isso a tela
  // mostraria velas do Aviator 1 sob o cabeçalho do Aviator 2 — dado errado, não
  // dado velho.
  watch(resolvedGameName, () => {
    detach();
    attach();
  });

  onMounted(attach);
  onBeforeUnmount(detach);

  const data = computed(() => poller.value?.data.value ?? null);
  const status = computed<CrashPreGameStatus>(
    () => poller.value?.status.value ?? 'idle',
  );

  const errorKind = computed(() => poller.value?.errorKind.value ?? null);

  const updatedAtMs = computed(() => {
    // ARMADILHA DO CONTRATO: `updatedAt` NUNCA chega null no HTTP. O core
    // calcula `$sample->first()?->round_time` (null sem rodada), mas o
    // CrashPreGameResource troca por `now()` antes de serializar. Então num jogo
    // sem vela nenhuma o campo diz "agora" a cada poll — o que faria `isStale`
    // nunca disparar e a tela anunciar "ao vivo há 0s" pra um coletor parado.
    // Sem vela, aqui não há horário nenhum a mostrar.
    if (!data.value?.cycleHistory.length) return null;

    if (!data.value?.updatedAt) return null;

    const parsed = Date.parse(data.value.updatedAt);

    return Number.isNaN(parsed) ? null : parsed;
  });

  /** Segundos desde a última vela. null quando não há `updatedAt`. */
  const secondsSinceUpdate = computed(() => {
    if (updatedAtMs.value === null) return null;

    const now = poller.value?.now.value ?? Date.now();

    // Clamp em 0: relógio do cliente adiantado em relação ao servidor faria a
    // conta dar negativo e a tela mostrar "atualizado há -3s".
    return Math.max(0, Math.round((now - updatedAtMs.value) / 1000));
  });

  /** Histórico velho = coletor provavelmente parado. */
  const isStale = computed(() => {
    if (secondsSinceUpdate.value === null) return false;

    return secondsSinceUpdate.value > STALE_AFTER_SECONDS;
  });

  /** Velho demais pra mostrar: o painel inteiro sai do ar. */
  const isAbandoned = computed(() => {
    if (secondsSinceUpdate.value === null) return false;

    return secondsSinceUpdate.value > ABANDONED_AFTER_SECONDS;
  });

  /**
   * Skeleton: primeira busca, sem nada pra mostrar ainda — menos num jogo que
   * já se mostrou vazio nesta sessão, onde o skeleton só anunciaria um painel
   * que não vem.
   */
  const isFirstLoad = computed(() => {
    const name = resolvedGameName.value;

    if (!name || knownEmptyGames.has(name)) return false;

    return !data.value && status.value !== 'error';
  });

  /**
   * Coletor nunca rodou / jogo novo. Estado de "aguardando dados", NÃO de erro:
   * a request foi bem, o jogo é que não tem histórico ainda.
   */
  const isEmpty = computed(
    () => Boolean(data.value) && data.value?.cycleHistory.length === 0,
  );

  /** Erro cheio: falhou e nunca houve um 200 pra deixar na tela. */
  const isFatalError = computed(() => Boolean(errorKind.value) && !data.value);

  /** Falhou o último tick, mas há payload anterior em tela. */
  const isStaleByError = computed(
    () => Boolean(errorKind.value) && Boolean(data.value),
  );

  function retry() {
    poller.value?.retry();
  }

  return {
    data,
    status,
    errorKind,
    isFirstLoad,
    isEmpty,
    isFatalError,
    isStaleByError,
    isStale,
    isAbandoned,
    secondsSinceUpdate,
    retry,
  };
}
